import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { dateEs, roleLabel } from "@/lib/formatters";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Copy, RefreshCw, Trash2, Edit, ShieldCheck, KeyRound } from "lucide-react";

const PERM_LABELS = {
  quotes_read: "Ver cotizaciones",
  quotes_write: "Crear/editar cotizaciones",
  quotes_delete: "Eliminar cotizaciones",
  clients_read: "Ver clientes",
  clients_write: "Crear/editar clientes",
  clients_delete: "Eliminar clientes",
  finance_read: "Ver finanzas",
  finance_write: "Registrar finanzas",
  calendar_read: "Ver agenda",
  calendar_write: "Agendar trabajos",
  reports_read: "Ver reportes de servicio",
  reports_write: "Crear reportes de servicio",
  users_manage: "Gestionar usuarios",
  company_config: "Configuración de empresa",
  backup: "Descargar respaldos",
};

export default function UsersList() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ email: "", name: "", role: "vendedor" });
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/users")).data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newUser.email || !newUser.name) return toast.error("Nombre y correo requeridos");
    try {
      const r = await api.post("/users", newUser);
      toast.success("Usuario creado. Copia el link de invitación.");
      setCreating(false);
      setNewUser({ email: "", name: "", role: "vendedor" });
      setEditing(r.data);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const savePerms = async () => {
    try {
      await api.patch(`/users/${editing.id}`, {
        name: editing.name, role: editing.role, active: editing.active,
        permissions: editing.permissions,
      });
      toast.success("Usuario actualizado");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const resetInv = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/reset-invitation`);
      setEditing(r.data);
      toast.success("Nuevo link de invitación generado");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`¿Eliminar usuario ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const copyInv = (link) => {
    if (!link) return;
    navigator.clipboard.writeText(link);
    toast.success("Link de invitación copiado");
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="users-list-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="pe-label">SEGURIDAD Y ACCESO</div>
          <h1 className="text-3xl sm:text-4xl font-black text-[color:var(--pe-primary)]">Usuarios y permisos</h1>
          <p className="text-slate-500 mt-1 text-sm">
            El administrador nunca establece contraseñas. Los usuarios se activan por link de invitación.
          </p>
        </div>
        <Button onClick={() => setCreating(true)} className="pe-btn-primary text-white h-11" data-testid="btn-new-user">
          <Plus size={16} className="mr-1" /> Nuevo usuario
        </Button>
      </div>

      <div className="mt-6 pe-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[color:var(--pe-primary)] text-white text-left">
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wide">Usuario</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wide">Correo</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wide">Rol</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wide">Estado</th>
                <th className="px-4 py-3 font-bold text-xs uppercase tracking-wide">Creado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-slate-500">Cargando…</td></tr>
              ) : rows.map((u) => (
                <tr key={u.id} className="border-t border-[color:var(--pe-border)] hover:bg-slate-50" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 font-bold">{u.name}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className="pe-badge pe-badge-borrador">{roleLabel[u.role] || u.role}</span>
                  </td>
                  <td className="px-4 py-3">
                    {!u.has_password ? (
                      <span className="text-amber-600 text-xs flex items-center gap-1"><KeyRound size={12} /> Pendiente activación</span>
                    ) : u.active ? (
                      <span className="text-emerald-600 text-xs flex items-center gap-1"><ShieldCheck size={12} /> Activo</span>
                    ) : (
                      <span className="text-slate-500 text-xs">Desactivado</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{dateEs(u.created_at)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {u.invitation_link && (
                      <button onClick={() => copyInv(u.invitation_link)}
                              title="Copiar link de invitación"
                              className="p-1 text-[color:var(--pe-accent)]"
                              data-testid={`btn-copy-invite-${u.email}`}>
                        <Copy size={16} />
                      </button>
                    )}
                    <button onClick={() => setEditing({ ...u })} className="p-1 text-slate-600 hover:text-[color:var(--pe-primary)] ml-1"
                            data-testid={`btn-edit-user-${u.email}`}>
                      <Edit size={16} />
                    </button>
                    {u.id !== me?.id && (
                      <button onClick={() => remove(u)} className="p-1 text-red-500 hover:text-red-700 ml-1"
                              data-testid={`btn-delete-user-${u.email}`}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuevo usuario</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1">
              <Label className="pe-label">Nombre completo *</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} data-testid="new-user-name" />
            </div>
            <div className="space-y-1">
              <Label className="pe-label">Correo *</Label>
              <Input type="email" value={newUser.email}
                     onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} data-testid="new-user-email" />
            </div>
            <div className="space-y-1">
              <Label className="pe-label">Rol</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrador</SelectItem>
                  <SelectItem value="vendedor">Vendedor</SelectItem>
                  <SelectItem value="tecnico">Técnico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-sky-50 border border-sky-200 rounded-md p-3 text-xs text-sky-800">
              Al crear el usuario se generará un <b>link de invitación</b> único que debes compartir manualmente
              (email quedará automatizado en fase posterior). El usuario define su propia contraseña.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancelar</Button>
            <Button onClick={create} className="pe-btn-primary text-white" data-testid="btn-create-user">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Editar usuario</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 mt-2">
              {editing.invitation_link && (
                <div className="pe-card p-3 bg-amber-50 border-amber-200">
                  <div className="pe-label mb-1">LINK DE INVITACIÓN</div>
                  <div className="text-xs break-all">{editing.invitation_link}</div>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => copyInv(editing.invitation_link)} data-testid="btn-copy-invite-edit">
                      <Copy size={14} className="mr-1" /> Copiar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => resetInv(editing)} data-testid="btn-reset-invite">
                      <RefreshCw size={14} className="mr-1" /> Regenerar
                    </Button>
                  </div>
                </div>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label className="pe-label">Nombre</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                </div>
                <div>
                  <Label className="pe-label">Rol</Label>
                  <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="vendedor">Vendedor</SelectItem>
                      <SelectItem value="tecnico">Técnico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="switch-user-active" />
                <span>Cuenta activa</span>
              </div>

              <div>
                <div className="pe-label mb-2">Permisos por módulo</div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {Object.entries(PERM_LABELS).map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox"
                             checked={!!editing.permissions?.[k]}
                             onChange={(e) => setEditing({
                               ...editing,
                               permissions: { ...editing.permissions, [k]: e.target.checked },
                             })}
                             className="h-4 w-4 accent-[color:var(--pe-accent)]" data-testid={`perm-${k}`} />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {editing.has_password && (
                <Button variant="outline" onClick={() => resetInv(editing)}>
                  <RefreshCw size={14} className="mr-1" /> Restablecer contraseña (nuevo link)
                </Button>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={savePerms} className="pe-btn-primary text-white" data-testid="btn-save-user">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
